import React, { useMemo, useState } from "react";

// shadcn/ui
import {
  Field,
  FieldDescription,
  FieldLabel,
  FieldSet,
  FieldError,
} from "@/components/ui/field";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

// Your domain types + builders from the previous message
import type {
  FormSchema,
  FormField,
  FormSection,
  FormObjectField,
  FormArrayField,
} from "./form-schema";
import {
  buildJoiSchema,
  evaluateVisibleIf,
  filterHiddenFieldErrors,
  type CustomValidatorMap,
} from "./joi-from-form-schema";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Separator } from "@/components/ui/separator";

/** Public props */
export type DynamicFormProps = {
  schema: FormSchema;
  initialValues?: Record<string, any>;
  onSubmit?: (values: Record<string, any>) => void | Promise<void>;
  customValidators?: CustomValidatorMap;
  submitLabel?: string;
};

/** Utility: init defaults from schema */
function getInitialValues(schema: FormSchema, provided?: Record<string, any>) {
  const out: Record<string, any> = { ...(provided ?? {}) };
  for (const section of schema.sections) {
    for (const f of flattenFields(section.fields)) {
      if (out[f.id] === undefined && "defaultValue" in f)
        out[f.id] = (f as any).defaultValue;
    }
  }
  return out;
}

function flattenFields(fields: FormField[]): FormField[] {
  const out: FormField[] = [];
  for (const f of fields) {
    out.push(f);
    if (f.kind === "object") {
      const of = f as FormObjectField;
      out.push(...flattenFields(of.fields));
    }
  }
  return out;
}

/** Component */
export default function DynamicForm({
  schema,
  initialValues,
  onSubmit,
  customValidators,
  submitLabel = "Save",
}: DynamicFormProps) {
  const [values, setValues] = useState<Record<string, any>>(
    getInitialValues(schema, initialValues)
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const joi = useMemo(
    () => buildJoiSchema(schema, customValidators),
    [schema, customValidators]
  );

  // validate all visible fields
  function validateAll(
    nextValues: Record<string, any>
  ): Record<string, string> {
    const result = joi.validate(nextValues, { abortEarly: false });
    const filtered = filterHiddenFieldErrors(schema, nextValues, result.error);
    const map: Record<string, string> = {};
    if (filtered) {
      for (const d of filtered.details) {
        const id = String(d.path?.[0] ?? "");
        const msg = d.message.replace(/^"|"$/g, "");
        if (!map[id]) map[id] = msg;
      }
    }
    return map;
  }

  function setValue(id: string, v: any) {
    setValues((prev) => {
      const next = { ...prev, [id]: v };
      setErrors(validateAll(next));
      return next;
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    e.stopPropagation();

    const currentErrors = validateAll(values);
    setErrors(currentErrors);
    if (Object.keys(currentErrors).length === 0) {
      await onSubmit?.(values);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {schema.sections.map((section) => (
        <SectionView
          key={section.id}
          section={section}
          values={values}
          errors={errors}
          setValue={setValue}
        />
      ))}

      <div className="pt-2 flex justify-end">
        <Button type="submit">{submitLabel}</Button>
      </div>
    </form>
  );
}

/** Section (supports 1-3 columns via Tailwind grid) */
function SectionView({
  section,
  values,
  errors,
  setValue,
}: {
  section: FormSection;
  values: Record<string, any>;
  errors: Record<string, string>;
  setValue: (id: string, v: any) => void;
}) {
  if (!evaluateVisibleIf(section.visibleIf, values)) return null;
  const cols = section.columns ?? 1;
  const grid =
    cols === 3 ? "grid-cols-3" : cols === 2 ? "grid-cols-2" : "grid-cols-1";

  return (
    <FieldSet>
      {section.title ? (
        <legend className="mb-1 text-lg font-medium">{section.title}</legend>
      ) : null}
      {section.description ? (
        <p className="mb-4 text-sm text-muted-foreground">
          {section.description}
        </p>
      ) : null}

      <div className={`grid gap-4 ${grid}`}>
        {section.fields.map((f) => (
          <FieldView
            key={f.id}
            field={f}
            values={values}
            errors={errors}
            setValue={setValue}
          />
        ))}
      </div>
    </FieldSet>
  );
}

/** Individual field renderer backed by shadcn Field */
function FieldView({
  field,
  values,
  errors,
  setValue,
}: {
  field: FormField;
  values: Record<string, any>;
  errors: Record<string, string>;
  setValue: (id: string, v: any) => void;
}) {
  if (!evaluateVisibleIf(field.visibleIf, values)) return null;

  const widthClass =
    field.width === "1/2"
      ? "col-span-1 md:col-span-1"
      : field.width === "1/3"
      ? "col-span-1"
      : field.width === "1/4"
      ? "col-span-1"
      : "col-span-1"; // full by default; grid is managed at section level

  const err = errors[field.id];
  const common = {
    id: field.id,
    disabled: field.disabled,
    placeholder: field.placeholder,
  } as const;

  switch (field.kind) {
    case "text": {
      const v = (values[field.id] ?? "") as string;
      return (
        <div className={widthClass}>
          <Field>
            <FieldLabel htmlFor={field.id}>{field.label}</FieldLabel>
            <Input
              {...common}
              type="text"
              inputMode={field.inputMode}
              value={v}
              onChange={(e) => setValue(field.id, e.target.value)}
            />
            {field.description ? (
              <FieldDescription>{field.description}</FieldDescription>
            ) : null}
            {err ? <FieldError>{err}</FieldError> : null}
          </Field>
        </div>
      );
    }
    case "textarea": {
      const v = (values[field.id] ?? "") as string;
      return (
        <div className={widthClass}>
          <Field>
            <FieldLabel htmlFor={field.id}>{field.label}</FieldLabel>
            <Textarea
              {...common}
              rows={field.rows ?? 4}
              value={v}
              onChange={(e) => setValue(field.id, e.target.value)}
            />
            {field.description ? (
              <FieldDescription>{field.description}</FieldDescription>
            ) : null}
            {err ? <FieldError>{err}</FieldError> : null}
          </Field>
        </div>
      );
    }
    case "number": {
      const v = values[field.id] as number | undefined;
      return (
        <div className={widthClass}>
          <Field>
            <FieldLabel htmlFor={field.id}>{field.label}</FieldLabel>
            <Input
              {...common}
              type="number"
              step={(field as any).step ?? 1}
              value={v ?? ""}
              onChange={(e) =>
                setValue(
                  field.id,
                  e.target.value === "" ? undefined : Number(e.target.value)
                )
              }
            />
            {field.description ? (
              <FieldDescription>{field.description}</FieldDescription>
            ) : null}
            {err ? <FieldError>{err}</FieldError> : null}
          </Field>
        </div>
      );
    }
    case "boolean": {
      const v = Boolean(values[field.id]);
      return (
        <div className={widthClass}>
          <Field>
            <div className="flex items-center justify-between">
              <FieldLabel htmlFor={field.id}>{field.label}</FieldLabel>
              <Switch
                id={field.id}
                checked={v}
                onCheckedChange={(c) => setValue(field.id, c)}
                disabled={field.disabled}
              />
            </div>
            {field.description ? (
              <FieldDescription>{field.description}</FieldDescription>
            ) : null}
            {err ? <FieldError>{err}</FieldError> : null}
          </Field>
        </div>
      );
    }
    case "select": {
      const f = field as any;
      const multiple = Boolean(f.multiple);
      const v = values[field.id];

      if (multiple) {
        const current: Array<string | number> = Array.isArray(v) ? v : [];
        return (
          <div className={widthClass}>
            <Field>
              <FieldLabel htmlFor={field.id}>{field.label}</FieldLabel>
              <MultiSelect
                allowCreate={f.allowCreate}
                id={field.id}
                disabled={field.disabled}
                placeholder={field.placeholder ?? "Select"}
                options={f.options}
                value={current}
                onChange={(next) => setValue(field.id, next)}
              />
              {field.description ? (
                <FieldDescription>{field.description}</FieldDescription>
              ) : null}
              {err ? <FieldError>{err}</FieldError> : null}
            </Field>
          </div>
        );
      }

      const sv = v === undefined || v === null ? "" : String(v);
      return (
        <div className={widthClass}>
          <Field>
            <FieldLabel htmlFor={field.id}>{field.label}</FieldLabel>
            <Autocomplete
              id={field.id}
              options={f.options}
              value={sv}
              onChange={(val) =>
                setValue(
                  field.id,
                  val == null ? null : castValue(String(val), f.options)
                )
              }
              disabled={field.disabled}
              placeholder={field.placeholder ?? "Select"}
              allowCreate={f.allowCreate}
            />
            {field.description ? (
              <FieldDescription>{field.description}</FieldDescription>
            ) : null}
            {err ? <FieldError>{err}</FieldError> : null}
          </Field>
        </div>
      );
    }
    case "radio": {
      const f = field as any;
      const v = values[field.id];
      const sv = v === undefined || v === null ? "" : String(v);
      return (
        <div className={widthClass}>
          <Field>
            <FieldLabel>{field.label}</FieldLabel>
            <RadioGroup
              value={sv}
              onValueChange={(val) =>
                setValue(field.id, castValue(val, f.options))
              }
            >
              <div className="grid gap-2">
                {f.options.map((o: any) => (
                  <label
                    key={String(o.value)}
                    className="flex items-center gap-2"
                  >
                    <RadioGroupItem
                      value={String(o.value)}
                      id={`${field.id}-${String(o.value)}`}
                      disabled={o.disabled || field.disabled}
                    />
                    <span>{o.label}</span>
                  </label>
                ))}
              </div>
            </RadioGroup>
            {field.description ? (
              <FieldDescription>{field.description}</FieldDescription>
            ) : null}
            {err ? <FieldError>{err}</FieldError> : null}
          </Field>
        </div>
      );
    }
    case "date": {
      const v = (values[field.id] ?? "") as string;
      return (
        <div className={widthClass}>
          <Field>
            <FieldLabel htmlFor={field.id}>{field.label}</FieldLabel>
            <Input
              {...common}
              type="date"
              value={v}
              onChange={(e) => setValue(field.id, e.target.value)}
              min={(field as any).min}
              max={(field as any).max}
            />
            {field.description ? (
              <FieldDescription>{field.description}</FieldDescription>
            ) : null}
            {err ? <FieldError>{err}</FieldError> : null}
          </Field>
        </div>
      );
    }
    case "json": {
      const raw = values[field.id];
      const v =
        typeof raw === "string"
          ? raw
          : raw == null
          ? ""
          : JSON.stringify(raw, null, (field as any).pretty ? 2 : 0);
      return (
        <div className={widthClass}>
          <Field>
            <FieldLabel htmlFor={field.id}>{field.label}</FieldLabel>
            <Textarea
              id={field.id}
              disabled={field.disabled}
              rows={8}
              value={v}
              onChange={(e) => setValue(field.id, e.target.value)}
              placeholder={field.placeholder ?? '{\n  "key": "value"\n}'}
            />
            {field.description ? (
              <FieldDescription>{field.description}</FieldDescription>
            ) : null}
            {err ? <FieldError>{err}</FieldError> : null}
          </Field>
        </div>
      );
    }
    case "object": {
      const of = field as FormObjectField;
      return (
        <div className="col-span-full">
          <FieldSet>
            <legend className="mb-1 text-base font-medium">
              {field.label}
            </legend>
            {field.description ? (
              <p className="mb-4 text-sm text-muted-foreground">
                {field.description}
              </p>
            ) : null}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {of.fields.map((child) => (
                <FieldView
                  key={child.id}
                  field={child}
                  values={values}
                  errors={errors}
                  setValue={setValue}
                />
              ))}
            </div>
            {errors[field.id] ? (
              <FieldError>{errors[field.id]}</FieldError>
            ) : null}
          </FieldSet>
        </div>
      );
    }
    case "array": {
      const af = field as FormArrayField;
      const items: any[] = Array.isArray(values[field.id])
        ? values[field.id]
        : [];
      const onAdd = () => {
        const next = [...items, defaultFor(af.of)];
        setValue(field.id, next);
      };
      const onRemove = (idx: number) => {
        const next = items.filter((_, i) => i !== idx);
        setValue(field.id, next);
      };
      return (
        <div className="col-span-full">
          <Field>
            <FieldLabel>{field.label}</FieldLabel>
            {field.description ? (
              <FieldDescription>{field.description}</FieldDescription>
            ) : null}
            <div className="space-y-3">
              {items.map((_, i) => (
                <div key={i} className="rounded-md border p-3">
                  <FieldView
                    field={af.of}
                    values={{ ...values, [af.of.id]: items[i] }}
                    errors={{
                      ...errors,
                      [af.of.id]: errors[`${field.id}.${i}`],
                    }}
                    setValue={(_, v) => {
                      const next = items.slice();
                      next[i] = v;
                      setValue(field.id, next);
                    }}
                  />
                  <div className="mt-2 flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => onRemove(i)}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
              <Button type="button" variant="secondary" onClick={onAdd}>
                Add {field.label}
              </Button>
            </div>
            {errors[field.id] ? (
              <FieldError>{errors[field.id]}</FieldError>
            ) : null}
          </Field>
        </div>
      );
    }
  }
}

/** Helpers */
function castValue(val: string, options: Array<{ value: string | number }>) {
  // If original option was a number, cast back to number
  const opt = options.find((o) => String(o.value) === val);
  if (!opt) return val;
  return typeof opt.value === "number" ? Number(val) : val;
}

function defaultFor(field: FormField): any {
  if ((field as any).defaultValue !== undefined)
    return (field as any).defaultValue;
  switch (field.kind) {
    case "text":
    case "textarea":
    case "date":
      return "";
    case "number":
      return undefined;
    case "boolean":
      return false;
    case "select":
    case "radio":
      return undefined;
    case "json":
      return "";
    case "object":
      return {};
    case "array":
      return [];
  }
}

type SelectOption = {
  value: string | number;
  label: string;
  disabled?: boolean;
};

function MultiSelect({
  id,
  options,
  value,
  onChange,
  placeholder,
  disabled,
  allowCreate = false,
}: {
  id: string;
  options: SelectOption[];
  value: Array<string | number>;
  onChange: (next: Array<string | number>) => void;
  placeholder?: string;
  disabled?: boolean;
  /** allow user to add new values that are not in options */
  allowCreate?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const selectedSet = new Set(value.map(String));

  const toggle = (raw: string) => {
    const opt = options.find((o) => String(o.value) === raw);
    const val = opt ? (typeof opt.value === "number" ? Number(raw) : raw) : raw;

    const next = new Set(value.map(String));
    if (next.has(String(val))) next.delete(String(val));
    else next.add(String(val));

    const out = Array.from(next).map((k) => {
      const o = options.find((x) => String(x.value) === k);
      return o ? o.value : k; // falls back to free-text value
    });

    onChange(out);
  };

  const clearAll = () => onChange([]);

  const canCreate =
    allowCreate &&
    search.trim().length > 0 &&
    !options.some((o) => {
      const s = search.trim().toLowerCase();
      return o.label.toLowerCase() === s || String(o.value).toLowerCase() === s;
    });

  const handleCreate = () => {
    const label = search.trim();
    if (!label) return;

    // just push the raw string into value – no need to persist in options
    onChange([...value, label]);
    setSearch("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between px-3"
        >
          <div className="flex flex-wrap items-center gap-1 text-left">
            {value.length === 0 ? (
              <span className="text-muted-foreground">
                {placeholder ?? "Select"}
              </span>
            ) : (
              value.map((v) => {
                const opt = options.find((o) => String(o.value) === String(v));
                return (
                  <Badge
                    key={String(v)}
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    {opt?.label ?? String(v)}
                    <button
                      type="button"
                      aria-label={`Remove ${opt?.label ?? v}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggle(String(v));
                      }}
                      className="ml-1 inline-flex"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                );
              })
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
      >
        <Command shouldFilter>
          <div className="p-2">
            <CommandInput
              placeholder="Search…"
              value={search}
              onValueChange={setSearch}
            />
          </div>

          <CommandEmpty className="px-3 py-2 text-sm text-muted-foreground">
            {canCreate ? (
              <div className="flex items-center justify-between gap-2">
                <span>
                  No results. Create{" "}
                  <span className="font-medium">
                    &quot;{search.trim()}&quot;
                  </span>
                  ?
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleCreate}
                >
                  Add
                </Button>
              </div>
            ) : (
              "No results"
            )}
          </CommandEmpty>

          <CommandList className="max-h-60 overflow-y-auto">
            <CommandGroup>
              {options.map((o) => {
                const selected = selectedSet.has(String(o.value));
                return (
                  <CommandItem
                    key={String(o.value)}
                    value={String(o.label)}
                    onSelect={() => !o.disabled && toggle(String(o.value))}
                    disabled={o.disabled}
                    className="cursor-pointer"
                  >
                    <Check
                      className={`mr-2 h-4 w-4 ${
                        selected ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    <span>{o.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>

          <Separator />
          <div className="flex items-center justify-between p-2">
            <span className="text-xs text-muted-foreground">
              {value.length} selected
            </span>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={clearAll}
              disabled={value.length === 0}
            >
              Clear
            </Button>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function Autocomplete({
  id,
  options,
  value,
  onChange,
  placeholder,
  disabled,
  allowCreate = false,
}: {
  id: string;
  options: SelectOption[];
  value: string | number | null | undefined;
  onChange: (next: string | number | null) => void;
  placeholder?: string;
  disabled?: boolean;
  /** allow user to add new values that are not in options */
  allowCreate?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const stringValue = value == null ? "" : String(value);
  const selectedOption = options.find((o) => String(o.value) === stringValue);

  const handleSelect = (raw: string) => {
    const opt = options.find((o) => String(o.value) === raw);
    const next = opt
      ? typeof opt.value === "number"
        ? Number(raw)
        : raw
      : raw;

    onChange(next);
    setOpen(false);
    setSearch("");
  };

  const clearValue = () => {
    onChange(null);
    setSearch("");
  };

  const canCreate =
    allowCreate &&
    search.trim().length > 0 &&
    !options.some((o) => {
      const s = search.trim().toLowerCase();
      return o.label.toLowerCase() === s || String(o.value).toLowerCase() === s;
    });

  const handleCreate = () => {
    const label = search.trim();
    if (!label) return;
    // we treat created values as plain strings, not persisted in `options`
    onChange(label);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between px-3"
        >
          <span className="truncate text-left">
            {selectedOption
              ? selectedOption.label
              : value != null && value !== ""
              ? String(value)
              : placeholder ?? "Select"}
          </span>
          <div className="flex items-center gap-1">
            {/* {value != null && value !== "" && (
              <X
                className="h-3 w-3 opacity-60 hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  clearValue();
                }}
              />
            )} */}
            <ChevronsUpDown className="ml-1 h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
      >
        <Command shouldFilter>
          <div className="p-2">
            <CommandInput
              placeholder="Search…"
              value={search}
              onValueChange={setSearch}
            />
          </div>

          <CommandEmpty className="px-3 py-2 text-sm text-muted-foreground">
            {canCreate ? (
              <div className="flex items-center justify-between gap-2">
                <span>
                  No results. Create{" "}
                  <span className="font-medium">
                    &quot;{search.trim()}&quot;
                  </span>
                  ?
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleCreate}
                >
                  Add
                </Button>
              </div>
            ) : (
              "No results"
            )}
          </CommandEmpty>

          <CommandList className="max-h-60 overflow-y-auto">
            <CommandGroup>
              {options.map((o) => {
                const isSelected = String(o.value) === stringValue;
                return (
                  <CommandItem
                    key={String(o.value)}
                    value={String(o.label)}
                    onSelect={() =>
                      !o.disabled && handleSelect(String(o.value))
                    }
                    disabled={o.disabled}
                    className="cursor-pointer"
                  >
                    <Check
                      className={`mr-2 h-4 w-4 ${
                        isSelected ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    <span>{o.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
